import React, { useCallback, useEffect, useState } from 'react';
import './Publisher.scss';
import { IPublisher } from '../../../../data/models/track';
import Publisher from './Publisher';
import { IMixedField } from '@/data/models/trackMixed';
import _, { isEmpty, isEqual, isNil } from 'lodash';
import MultiTrackChangeIndicator from '@/components/common/MultiTrackChangeIndicator/MultiTrackChangeIndicator';

interface PublisherProps {
  publisher: IPublisher[] | IMixedField[] | null | undefined;
  originalPublisher?: IMixedField[] | null | undefined;
  handlePublisher: any;
  errorBanner: boolean;
  validationState?: string;
  readonly?: boolean;
  disabled?: boolean;
  multiTrack?: boolean;
  showChangeIndicator?: boolean;
  onFocusPublisher?: () => void;
  onFocusPRO?: () => void;
}

export default function PublisherContainer({
  publisher,
  originalPublisher,
  handlePublisher,
  errorBanner,
  validationState,
  readonly,
  disabled,
  multiTrack = false,
  showChangeIndicator = false,
  onFocusPublisher,
  onFocusPRO,
}: PublisherProps) {
  const [inputs, setInputs] = useState<any>(publisher);

  const handleAddInput = () => {
    let newPublisher = [];
    if (multiTrack) {
      newPublisher = [
        ...inputs.map((a: any) => ({ ...a })),
        {
          value: { performing_Rights_Organization: '', name: '' },
          originalValue: null,
          isMixed: false,
          isReadOnly: false,
        },
      ];
    } else {
      newPublisher = [
        ...inputs.map((a: any) => ({ ...a })),
        { performing_Rights_Organization: '', name: '' },
      ];
    }

    setInputs(newPublisher);
    handlePublisher(newPublisher);
  };

  useEffect(() => {
    if (!isNil(publisher) && !isEmpty(publisher)) {
      setInputs(publisher);
    } else {
      if (multiTrack) {
        setInputs([
          {
            value: { performing_Rights_Organization: '', name: '' },
            originalValue: null,
            isMixed: false,
            isReadOnly: false,
          },
        ]);
      } else {
        setInputs([{ performing_Rights_Organization: '', name: '' }]);
      }
    }
  }, [publisher]);

  const handleChange = (event: any, index: any, field: string) => {
    const { value } = event.target;
    const onChangeValue = _.cloneDeep(inputs);

    if (multiTrack) {
      if (!onChangeValue[index].value) {
        onChangeValue[index].value = {
          performing_Rights_Organization: field !== 'name' ? value : null,
          name: field === 'name' ? value : null,
        };
      } else {
        onChangeValue[index].value[field] = value;
      }
    } else {
      onChangeValue[index][field] = value;
    }

    setInputs(onChangeValue);
    handlePublisher(onChangeValue);
  };

  const handleDeleteInput = (index: any) => {
    const newArray = _.cloneDeep(inputs);

    if (newArray.length === 1) {
      if (multiTrack) {
        if (isEmpty(originalPublisher)) {
          return undoChanges();
        } else {
          newArray[0] = {
            value: { performing_Rights_Organization: '', name: '' },
            originalValue: { performing_Rights_Organization: '', name: '' },
            isMixed: false,
            isReadOnly: false,
          };
        }
      } else {
        newArray[0] = { performing_Rights_Organization: '', name: '' };
      }
    } else {
      newArray.splice(index, 1);
    }

    setInputs(newArray);
    handlePublisher(newArray);
  };

  const undoChanges = useCallback(() => {
    handlePublisher(_.cloneDeep(originalPublisher));
  }, [originalPublisher]);

  return (
    <>
      {multiTrack && showChangeIndicator ? (
        <MultiTrackChangeIndicator
          show={!isEqual(publisher, originalPublisher)}
          onClick={() => undoChanges()}
        ></MultiTrackChangeIndicator>
      ) : null}
      <div className="list-builder">
        {inputs?.map((item: any, index: any) => {
          let publisherItem = item;
          let isReadOnly = readonly;
          let customNameInputClass = null;
          let customPROInputClass = null;
          let isMixedNamePlaceholder = false;
          let isMixedRolePlaceholder = false;

          if (multiTrack) {
            if (item?.isReadOnly) {
              isReadOnly = item?.isReadOnly;
            }
            publisherItem = item.value;
            isMixedNamePlaceholder =
              item.isMixed &&
              publisherItem?.name === 'Mixed' &&
              item?.originalValue.name === 'Mixed';
            isMixedRolePlaceholder =
              item.isMixed &&
              publisherItem?.performing_Rights_Organization === 'Mixed' &&
              item?.originalValue.performing_Rights_Organization === 'Mixed';
            if (isMixedNamePlaceholder) {
              customNameInputClass = 'mixed-input-text';
            }
            if (isMixedRolePlaceholder) {
              customPROInputClass = 'mixed-input-text';
            }
          }

          if (!publisherItem) return;
          return (
            <div key={index} className="publisher">
              <Publisher
                index={index}
                item={publisherItem}
                errorBanner={errorBanner}
                validationState={validationState}
                handleChange={handleChange}
                inputs={inputs}
                inputNameCustomClass={customNameInputClass}
                inputPROCustomClass={customPROInputClass}
                handleDeleteInput={handleDeleteInput}
                handleAddInput={handleAddInput}
                readonly={isReadOnly}
                disabled={disabled}
                nameMixedPlaceholder={isMixedNamePlaceholder}
                roleMixedPlaceholder={isMixedRolePlaceholder}
                onFocusPublisher={onFocusPublisher}
                onFocusPRO={onFocusPRO}
              ></Publisher>
            </div>
          );
        })}
      </div>
    </>
  );
}
